import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  AppBar,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import { RESULTS_PER_PAGE } from "../../../../utils/constants";
import { TParamValues } from "../../../../hooks/useUrlQueryString";
import { makeKey } from "../../../../utils/helpers";

import { ErrorMessage } from "../../../../components/error-box/error-message/error-message.component";
import { ListingTable } from "../../../../components/layout/listing-table/listing-table.component";
import { Pagination } from "../../../../components/Pagination/pagination.component";
import { SearchIcon } from "../../../../components/icon/icons";
import { SpinnerLoader } from "../../../../components/loaders";
import {
  getCurrentPage,
  getOffset,
  getTotalPages,
} from "../../../../components/Pagination/pagination.functions";

import * as store from "../../store/app-search.store";
import { getCurrentTab, isSearchTermValid } from "../../types/app-search.functions";
import { sections } from "../../types/app-search-sections/app-search-sections.config";

import { AppSearchTransition } from "../app-search-transition/app-search-transition.component";

import { useAppSearchStyles } from "./app-search.styles";

const defaultFilters = { limit: RESULTS_PER_PAGE, offset: 0 };

export const AppSearch = () => {
  const { pathname } = useLocation();
  const currSection = getCurrentTab(pathname);
  const classes = useAppSearchStyles();
  const [open, setOpen] = React.useState(false);
  const [suggestionInputValue, setSuggestionInputValue] = useState<string>("");
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedTab, setSelectedTab] = useState<number>(currSection);
  const [pageCount, setPageCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const selectedSection = sections[selectedTab];

  const errors = useSelector(store.getErrors);
  const loaded = useSelector(store.isLoaded);
  const loading = useSelector(store.isLoading);
  const pagination = useSelector(store.getPagination);
  const results = useSelector(store.getResults);

  useEffect(() => {
    setPageCount(getTotalPages(pagination?.count));
  }, [pagination]);

  useEffect(() => {
    setCurrentPage(getCurrentPage(filters.offset));
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const toggleOpen = () => {
    setOpen(!open);
    reset();
  };

  const handleInputChange = (event: any) => {
    const searchString = event.target.value;
    setSuggestionInputValue(searchString);
  };

  const handlePageChange = (params: TParamValues) => {
    // @ts-ignore
    const nextPage: number = params.page[0];
    setFilters({ ...filters, offset: getOffset(nextPage) });
  };

  const handleSearch = (term?: string, params?: object) => {
    const searchTerm = term ?? suggestionInputValue;
    const searchParams = { searchTerm, ...(params ?? filters) };

    if (isSearchTermValid(searchTerm)) {
      // @ts-ignore
      store.search(selectedSection.endpoint, selectedSection.dataKey, searchParams);
    }
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    store.clear();
    setFilters(defaultFilters);
    setSelectedTab(newValue);
    setSuggestionInputValue("");
  };

  const onKeyPress = (event: any) => {
    if (event.key === "Enter") {
      setFilters({ ...filters, offset: 0 });
    }
  };

  const renderContents = () => {
    if (loading) {
      return <SpinnerLoader />;
    }

    if (errors) {
      return <ErrorMessage message={errors.errorMessage} title={errors.errorType ?? "Error"} />;
    }

    if (!loaded) {
      return null;
    }

    if (selectedTab === 0) {
      const tables = sections
        .map((section, i) => {
          const data = results[section.dataKey];

          if (data?.length) {
            return (
              <div key={makeKey("results", i)}>
                <Typography variant="h4">{section.label}</Typography>
                <ListingTable
                  className={classes.tables}
                  close={toggleOpen}
                  columns={section.columns}
                  dataList={data}
                  link={section.link}
                />
              </div>
            );
          } else {
            return null;
          }
        })
        .filter((table) => table);

      // Render empty, header-less table for No Results message
      return !!tables.length ? (
        tables
      ) : (
        <ListingTable className={classes.tables} columns={[]} dataList={[]} headers={false} />
      );
    }

    return (
      <>
        <ListingTable
          className={classes.tables}
          close={toggleOpen}
          columns={selectedSection.columns}
          dataList={results[selectedSection.dataKey]}
          link={selectedSection.link}
        />
        {renderPagination()}
      </>
    );
  };

  const renderPagination = () => {
    return (
      <Pagination
        callback={handlePageChange}
        controlledParams={{ page: currentPage }}
        count={pageCount}
      />
    );
  };

  const reset = () => {
    store.clear();
    setFilters(defaultFilters);
    setSelectedTab(currSection);
    setSuggestionInputValue("");
  };

  return (
    <>
      <IconButton onClick={toggleOpen} title="Search">
        <SearchIcon />
      </IconButton>
      <Dialog
        className={classes.dialog}
        fullScreen
        open={open}
        onClose={toggleOpen}
        scroll="paper"
        TransitionComponent={AppSearchTransition}
      >
        <AppBar className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <div className={classes.searchRow}>
              <TextField
                autoFocus
                className={classes.searchInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={handleInputChange}
                onKeyPress={onKeyPress}
                value={suggestionInputValue}
                variant="outlined"
              />
              <IconButton aria-label="close" edge="end" onClick={toggleOpen}>
                <CloseIcon fontSize="large" />
              </IconButton>
            </div>
            <Tabs indicatorColor="primary" onChange={handleTabChange} value={selectedTab}>
              {sections.map((s, i) => (
                <Tab className={classes.tab} key={i} label={s.label} tabIndex={0} />
              ))}
            </Tabs>
          </Toolbar>
        </AppBar>
        <DialogContent className={classes.dialogContent}>{renderContents()}</DialogContent>
      </Dialog>
    </>
  );
};
